import { UserFormValues } from '@/api/types';
import { ApiResponse, PageContent } from '@/types/api-response';
import { apiClient } from '@/api/api-client';
import { User } from '@/types/user';
import {
  normalizeCreateUserRequest,
  normalizeUpdateUserRequest,
  type UserCreateRequest,
  type UserUpdateRequest,
} from '@/utils/user-form-utils';

// Create a new user
export async function createUser(user: UserFormValues): Promise<ApiResponse<User>> {
  const requestData = normalizeCreateUserRequest(user);
  const response = await apiClient.post<User, UserCreateRequest>('/users', requestData);

  return response;
}

// Get all users with pagination and search support
export async function getUsers(params?: {
  page?: number;
  size?: number;
  searchTerm?: string | null;
}): Promise<ApiResponse<PageContent<User>>> {
  const { searchTerm, page = 0, size = 20 } = params || {};

  if (searchTerm?.trim()) {
    // Use search endpoint with pagination
    const response = await apiClient.get<PageContent<User>, object>('/users/search', {
      searchTerm: searchTerm.trim(),
      page,
      size,
    });
    return response;
  } else {
    // Use regular endpoint with pagination
    const response = await apiClient.get<PageContent<User>, object>('/users', {
      page,
      size,
    });
    return response;
  }
}

// Get user by ID
export async function getUser(id: string): Promise<ApiResponse<User>> {
  const response = await apiClient.get<User, object>(`/users/${id}`);
  return response;
}

// Update user by ID
export async function updateUser(id: string, user: UserFormValues): Promise<ApiResponse<User>> {
  const requestData = normalizeUpdateUserRequest(user);
  const response = await apiClient.put<User, UserUpdateRequest>(`/users/${id}`, requestData);
  return response;
}

// Delete user by ID
export async function deleteUser(id: string): Promise<ApiResponse<User>> {
  const response = await apiClient.delete<User>(`/users/${id}`);
  return response;
}

// Force logout user by ID
export async function forceLogout(id: string): Promise<ApiResponse<{}>> {
  const response = await apiClient.post<{}, {}>(`/users/${id}/force-logout`);
  return response;
}